import { useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Generates a simple device fingerprint from browser properties.
 * Not cryptographically unique, but sufficient for session tracking.
 */
function generateDeviceFingerprint(): string {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  let canvasHash = "no-canvas";
  if (ctx) {
    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillText("fingerprint", 2, 2);
    canvasHash = canvas.toDataURL().slice(-50);
  }

  const components = [
    navigator.language,
    screen.width + "x" + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || "unknown",
    canvasHash,
  ];

  // Simple hash
  const str = components.join("|");
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return "fp_" + Math.abs(hash).toString(36);
}

function parseUserAgent(ua: string) {
  let browser = "Unknown";
  let os = "Unknown";
  let platform = "Unknown";

  // Browser detection
  if (ua.includes("Chrome") && !ua.includes("Edg")) browser = "Chrome";
  else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari";
  else if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Edg")) browser = "Edge";

  // OS detection
  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac OS")) os = "macOS";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";
  else if (ua.includes("Linux")) os = "Linux";

  // Platform
  if (ua.includes("Mobile")) platform = "Mobile";
  else if (ua.includes("Tablet") || ua.includes("iPad")) platform = "Tablet";
  else platform = "Desktop";

  return { browser, os, platform };
}

/**
 * Hook to track user sessions with device fingerprint, IP, and user agent.
 * Automatically logs session on auth state change.
 */
export function useSessionTracking() {
  const logSession = useCallback(async (userId: string) => {
    try {
      const fingerprint = generateDeviceFingerprint();
      const userAgent = navigator.userAgent;
      const { browser, os, platform } = parseUserAgent(userAgent);

      await supabase.from("user_sessions").insert({
        user_id: userId,
        device_fingerprint: fingerprint,
        user_agent: userAgent.substring(0, 500), // Limit length
        browser,
        os,
        platform,
      });
    } catch (error) {
      // Silent fail - session tracking should not block user flow
      console.error("Session tracking error:", error);
    }
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "SIGNED_IN" && session?.user?.id) {
          logSession(session.user.id);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [logSession]);
}
